import { ChangeEvent, FC, InputHTMLAttributes } from 'react';

import { CountryCode } from 'libphonenumber-js';
import { CountryDropdown } from './components/CountryDropdown';

import { SkeletonOfAnySize } from '../../Skeletons';
import classNames from 'classnames';
import { usePhoneNumber } from './hooks/usePhoneNumber';
import { usePhoneInputHandlers } from './hooks/usePhoneInputHandlers';
import { useTranslation } from 'react-i18next';

interface InputPhoneProps extends InputHTMLAttributes<HTMLInputElement> {
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  countryCode: CountryCode;
  phone: string | undefined;
  isLoading?: boolean;
  errorMessage: string | undefined;
}

export const InputPhone: FC<InputPhoneProps> = ({
  onChange,
  countryCode,
  phone = '',
  isLoading = false,
  errorMessage,
  ...props
}) => {
  const {
    phoneNumber,
    selectedCountry,
    currentCountryCode,
    handleSelectCountry,
    handleBlur,
    handleFocus,
    setPhoneNumber
  } = usePhoneNumber({
    phone,
    isLoading,
    countryCode,
    disabled: Boolean(props.disabled) || Boolean(props.readOnly)
  });

  const { t } = useTranslation();

  const { handleInput, handleBeforeInput } = usePhoneInputHandlers({
    currentCountryCode,
    phoneNumber,
    setPhoneNumber,
    selectedCountry
  });

  if (isLoading) {
    return <SkeletonOfAnySize className="h-10 w-full bg-white rounded-[20px]" />;
  }

  return (
    <>
      <div
        className={classNames(
          props.className,
          (props.disabled || props.readOnly) && 'bg-i_bg_disabled',
          errorMessage && 'border-i_border_error',
          'flex flex-row items-center rounded-[20px] h568:relative w-full border border-i_border bg-i_bg max-h-10 px-5 py-3'
        )}>
        <CountryDropdown
          disabled={props.disabled || props.readOnly}
          selectedCountry={selectedCountry}
          onSelect={handleSelectCountry}
        />
        <input
          {...props}
          className="flex-1 outline-none read-only:pointer-events-none read-only:text-i_text_disabled  disabled:opacity-100 w-full placeholder:text-i_placeholder text-i_text ml-2 disabled:text-i_text_disabled read-only:bg-i_bg_disabled disabled:bg-i_bg_disabled"
          inputMode="tel"
          type="text"
          value={phoneNumber}
          onInput={handleInput}
          onBeforeInput={handleBeforeInput}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      </div>
      {errorMessage && <span className="text-map_error text-xs">{t(errorMessage)}</span>}
    </>
  );
};
